import React, { useState, useEffect, useMemo } from "react";

import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Pagination from "@material-ui/lab/Pagination";
import * as XLSX from "xlsx";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import FunnelChart from "../../components/FunnelChart";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";

import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { UsersFilter } from "../../components/UsersFilter";
import { StatusFilter } from "../../components/StatusFilter";
import useDashboard from "../../hooks/useDashboard";

import QueueSelectCustom from "../../components/QueueSelectCustom";
import moment from "moment";
import ShowTicketLogModal from "../../components/ShowTicketLogModal";
import PageHelpGuide from "../../components/PageHelpGuide";
import { REPORTS_HELP_DATA } from "../../constants/pageHelp/reportsHelp";

import { blue, green } from "@material-ui/core/colors";
import {
  AttachMoney,
  CheckCircle,
  Equalizer,
  Facebook,
  FilterList,
  Forward,
  History,
  Instagram,
  SaveAlt,
  TrendingDown,
  TrendingUp,
  WhatsApp,
} from "@material-ui/icons";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles((theme) => ({
  ...getStandardButtonStyles(theme),
  cyanButton: {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    borderRadius: '4px',
    textTransform: 'none',
    fontWeight: 'bold',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: `0 4px 12px ${theme.palette.primary.main}66`,
      transform: 'translateY(-2px)'
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: 'none'
    }
  },
  mainContainer: {
    background: theme.palette.fancyBackground,
    padding: theme.spacing(1),
  },
  mainPaper: {
    flex: 1,
    overflow: "hidden",
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32 !important" : theme.palette.background.paper,
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(1),
  },
  pageScrollArea: {
    ...theme.scrollbarStyles,
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(3),
  },
  mainPaperFilter: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32" : theme.palette.background.paper,
    marginBottom: theme.spacing(2.5),
    borderRadius: 12,
    "& .MuiOutlinedInput-root": {
      height: 40,
      minHeight: 40,
      maxHeight: 40,
      borderRadius: 8,
      fontSize: "0.82rem",
      boxSizing: "border-box",
      backgroundColor: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.03)" : "#FFFFFF",
      display: "flex",
      alignItems: "center",
      transition: "border-color 0.2s, box-shadow 0.2s",
      "&:hover": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused": {
        boxShadow: `${theme.palette.primary.main}40 0 0 8px`,
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "8px 12px",
      fontSize: "0.82rem",
      boxSizing: "border-box",
    },
    "& .MuiInputLabel-outlined": {
      fontSize: "0.82rem",
      transform: "translate(14px, 12px) scale(1)",
      "&.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
      },
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      height: "100%",
      padding: "0 28px 0 12px !important",
      boxSizing: "border-box",
    },
  },
  mainPaperTable: {
    minHeight: 440,
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.type === "dark" ? "#2A2D32" : theme.palette.background.paper,
    display: "flex",
    flexDirection: "column",
    borderRadius: 12,
    marginBottom: theme.spacing(2),
  },
  tableScrollArea: {
    minHeight: 380,
    overflowX: "hidden",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  tableHeaderCell: {
    verticalAlign: "middle",
    padding: "10px 8px",
    fontSize: "0.78rem",
    fontWeight: 700,
    lineHeight: 1.25,
    whiteSpace: "normal",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#1E293B",
  },
  tableRowCustom: {
    minHeight: 56,
  },
  kpiCard: {
    borderRadius: 12,
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.03)' : '#ffffff',
    backdropFilter: 'blur(8px)',
  },
  kpiTitle: {
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginBottom: 4,
  },
  kpiValue: {
    fontSize: '1.4rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  pipelineBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    overflowX: 'auto',
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: 12,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    border: `1px solid ${theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}`,
    ...theme.scrollbarStyles,
  },
  pipelineStage: {
    flex: '1 0 160px',
    padding: theme.spacing(1),
    borderRadius: 8,
    textAlign: 'center',
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : '#fff',
    borderLeft: '4px solid #00b0ff',
    boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
  },
  truncatedMessage: {
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    fontSize: "0.8rem",
  },
  tableCellMiddle: {
    verticalAlign: "middle",
    padding: "12px 8px",
  },
}));

const Reports = () => {
  const theme = useTheme();
  const classes = useStyles();
  const history = useHistory();
  const { getReport } = useDashboard();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Filtros Generales
  const [searchParam, setSearchParam] = useState("");
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [options, setOptions] = useState([]);
  const [dateFrom, setDateFrom] = useState(
    moment("1", "D").format("YYYY-MM-DD")
  );
  const [dateTo, setDateTo] = useState(moment().format("YYYY-MM-DD"));
  const [onlyRated, setOnlyRated] = useState(false);

  // Filtros Jerárquicos de Embudo
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [boards, setBoards] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");
  const [lanes, setLanes] = useState([]);
  const [selectedLane, setSelectedLane] = useState("");
  const [laneTags, setLaneTags] = useState([]);
  const [selectedLaneTags, setSelectedLaneTags] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [currency, setCurrency] = useState("USD $");

  // Métricas de Embudo Visual (Funnel Chart) y Lead Time
  const [funnelData, setFunnelData] = useState(null);
  const [loadingFunnel, setLoadingFunnel] = useState(false);

  const [totalTickets, setTotalTickets] = useState(0);
  const [tickets, setTickets] = useState([]);
  const [openTicketMessageDialog, setOpenTicketMessageDialog] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(null);

  // Cargar Marcas, Tableros, Conexiones y Moneda Inicial
  useEffect(() => {
    async function loadInitialData() {
      try {
        const [brandsRes, boardsRes, whatsappsRes, settingsRes] = await Promise.all([
          api.get("/brands"),
          api.get("/kanban-boards"),
          api.get("/whatsapp"),
          api.get("/settings"),
        ]);
        const brandsList = brandsRes.data.brands || brandsRes.data || [];
        const boardsList = boardsRes.data || [];
        setBrands(brandsList);
        setBoards(boardsList);
        setWhatsapps(whatsappsRes.data || []);

        const curr = settingsRes.data?.find((s) => s.key === "system_currency");
        if (curr && curr.value) {
          setCurrency(curr.value);
        }

        // Auto-selección inicial del embudo activo para que el gráfico aparezca de inmediato
        if (boardsList.length > 0) {
          const savedBoardId = localStorage.getItem('kanbanSelectedBoard');
          const defaultBoard = boardsList.find((b) => String(b.id) === String(savedBoardId)) || boardsList[0];
          if (defaultBoard) {
            setSelectedBoard(defaultBoard.id);
            if (defaultBoard.brandId) {
              setSelectedBrand(defaultBoard.brandId);
            }
            try {
              const { data: lanesData } = await api.get(`/tag/kanban`, {
                params: { kanban: 1, kanbanBoardId: defaultBoard.id, boardId: defaultBoard.id }
              });
              setLanes(Array.isArray(lanesData) ? lanesData : lanesData.lista || []);
            } catch (e) {
              console.error("Error al cargar etapas del tablero por defecto:", e);
            }
            fetchFunnelMetrics(defaultBoard.id);
            handleFilter(1, undefined, defaultBoard.id);
          }
        }
      } catch (err) {
        console.error("Error al cargar datos iniciales de filtros:", err);
      }
    }
    loadInitialData();
  }, []);

  // Carga automática inicial de atenciones al montar el componente (Mes actual)
  useEffect(() => {
    // Si no hay tableros o mientras carga loadInitialData
    handleFilter(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Obtener métricas del embudo visual y lead time
  const fetchFunnelMetrics = async (boardId = selectedBoard) => {
    if (!boardId) {
      setFunnelData(null);
      return;
    }
    setLoadingFunnel(true);
    try {
      const { data } = await api.get("/ticketreport/funnel-metrics", {
        params: {
          kanbanBoardId: boardId,
          brandId: selectedBrand || undefined,
          dateStart: dateFrom || undefined,
          dateEnd: dateTo || undefined,
          whatsappIds: selectedWhatsapp.length > 0 ? JSON.stringify(selectedWhatsapp) : undefined,
          queueIds: queueIds.length > 0 ? JSON.stringify(queueIds) : undefined,
          users: userIds.length > 0 ? JSON.stringify(userIds) : undefined,
        },
      });
      setFunnelData(data);
    } catch (err) {
      console.error("Error al obtener métricas del embudo:", err);
      setFunnelData(null);
    } finally {
      setLoadingFunnel(false);
    }
  };

  // Al cambiar de Marca, reiniciar embudo y subfiltros
  const handleBrandChange = (e) => {
    const brandId = e.target.value;
    setSelectedBrand(brandId);
    setSelectedBoard("");
    setSelectedLane("");
    setLanes([]);
    setLaneTags([]);
    setSelectedLaneTags([]);
    setFunnelData(null);
  };

  // Al cambiar de Embudo (Tablero), cargar sus etapas (lanes) y métricas de embudo
  const handleBoardChange = async (e) => {
    const boardId = e.target.value;
    setSelectedBoard(boardId);
    setSelectedLane("");
    setLaneTags([]);
    setSelectedLaneTags([]);

    if (boardId) {
      try {
        const { data } = await api.get(`/tag/kanban`, {
          params: { kanban: 1, kanbanBoardId: boardId, boardId }
        });
        setLanes(Array.isArray(data) ? data : data.lista || []);
      } catch (err) {
        console.error("Error al cargar etapas del tablero:", err);
      }
      fetchFunnelMetrics(boardId);
      handleFilter(1, "", boardId);
    } else {
      setLanes([]);
      setFunnelData(null);
      handleFilter(1, "", "");
    }
  };

  // Selección interactiva de etapa desde el embudo visual
  const handleStageSelect = (stageId) => {
    const nextLane = String(selectedLane) === String(stageId) ? "" : stageId;
    setSelectedLane(nextLane);
    setSelectedLaneTags([]);
    if (nextLane) {
      const activeLane = lanes.find((l) => String(l.id) === String(nextLane));
      if (activeLane && activeLane.kanbanLaneTags) {
        const subTags = activeLane.kanbanLaneTags.map((klt) => klt.tag).filter(Boolean);
        setLaneTags(subTags);
      } else {
        setLaneTags([]);
      }
    } else {
      setLaneTags([]);
    }
    handleFilter(1, nextLane);
  };

  // Al cambiar de Etapa, cargar sus sub-etiquetas correspondientes
  const handleLaneChange = (e) => {
    const laneId = e.target.value;
    setSelectedLane(laneId);
    setSelectedLaneTags([]);

    if (laneId) {
      const activeLane = lanes.find((l) => String(l.id) === String(laneId));
      if (activeLane && activeLane.kanbanLaneTags) {
        const subTags = activeLane.kanbanLaneTags.map((klt) => klt.tag).filter(Boolean);
        setLaneTags(subTags);
      } else {
        setLaneTags([]);
      }
    } else {
      setLaneTags([]);
    }
    handleFilter(1, laneId);
  };

  // Embudos filtrados por Marca seleccionada
  const filteredBoards = useMemo(() => {
    if (!selectedBrand) return boards;
    return boards.filter((b) => String(b.brandId) === String(selectedBrand));
  }, [boards, selectedBrand]);

  // Conexiones filtradas por Marca seleccionada
  const filteredWhatsapps = useMemo(() => {
    if (!selectedBrand) return whatsapps;
    return whatsapps.filter((w) => String(w.brandId) === String(selectedBrand));
  }, [whatsapps, selectedBrand]);

  // Cálculo de KPIs Comerciales
  const kpis = useMemo(() => {
    // Si hay datos del embudo activo, sincronizar prioritariamente con el embudo
    if (funnelData && Array.isArray(funnelData.stages) && funnelData.stages.length > 0) {
      const activeStages = selectedLane
        ? funnelData.stages.filter((s) => String(s.id) === String(selectedLane))
        : funnelData.stages;

      let totalRevenue = 0;
      let lostRevenue = 0;
      let lostCount = 0;
      let dealsCount = 0;
      let wonDeals = 0;
      let totalStageCount = 0;

      activeStages.forEach((st) => {
        const val = parseFloat(st.totalValue) || 0;
        const count = parseInt(st.count, 10) || 0;
        const withVal = st.ticketsWithValue !== undefined ? (parseInt(st.ticketsWithValue, 10) || 0) : (val > 0 ? count : 0);

        totalStageCount += count;

        if (st.stageRole === "won") {
          wonDeals += count;
        } else if (st.stageRole === "lost") {
          lostCount += count;
          lostRevenue += val;
        } else {
          // Etapas en progreso / negociación activa en embudo
          totalRevenue += val;
          dealsCount += withVal;
        }
      });

      const totalCount = selectedLane
        ? totalStageCount
        : (funnelData.totalLeads !== undefined ? funnelData.totalLeads : (totalTickets || 0));

      const conversionRate = selectedLane
        ? (totalCount > 0 ? ((wonDeals / totalCount) * 100).toFixed(1) : "0.0")
        : (funnelData.overallConversion !== undefined
            ? Number(funnelData.overallConversion).toFixed(1)
            : (totalCount > 0 ? ((wonDeals / totalCount) * 100).toFixed(1) : "0.0"));

      // Ticket Promedio: suma de tickets con valor dividida entre el número de tickets con valor
      let avgTicket = "0.00";
      if (dealsCount > 0) {
        avgTicket = (totalRevenue / dealsCount).toFixed(2);
      } else if (funnelData.totalTicketsWithValue > 0 && funnelData.totalValue > 0) {
        avgTicket = (funnelData.totalValue / funnelData.totalTicketsWithValue).toFixed(2);
      }

      return {
        totalCount,
        totalRevenue: totalRevenue.toFixed(2),
        lostRevenue: lostRevenue.toFixed(2),
        lostCount,
        conversionRate,
        avgTicket,
        wonDeals,
      };
    }

    const count = totalTickets || 0;
    let totalRevenue = 0;
    let lostRevenue = 0;
    let lostCount = 0;
    let dealsCount = 0;
    let wonDeals = 0;

    tickets.forEach((t) => {
      const val = parseFloat(t.opportunityValue || t.valorVenda);
      const hasVal = !isNaN(val) && val > 0;
      const isWon = t.stageRole === "won" || t.finalizadoComVenda === true;
      const isLost = t.stageRole === "lost" || (Boolean(t.motivoNaoVenda) && !isWon);

      if (isWon) {
        wonDeals += 1;
      } else if (isLost) {
        lostCount += 1;
        if (hasVal) {
          lostRevenue += val;
        }
      } else {
        // Etapas en progreso / negociación activa en embudo
        if (hasVal) {
          totalRevenue += val;
          dealsCount += 1;
        }
      }
    });

    const conversionRate = count > 0 ? ((wonDeals / count) * 100).toFixed(1) : "0.0";
    const avgTicket = dealsCount > 0 ? (totalRevenue / dealsCount).toFixed(2) : "0.00";

    return {
      totalCount: count,
      totalRevenue: totalRevenue.toFixed(2),
      lostRevenue: lostRevenue.toFixed(2),
      lostCount,
      conversionRate,
      avgTicket,
      wonDeals,
    };
  }, [funnelData, selectedLane, totalTickets, tickets]);

  const translateStatus = (status) => {
    if (!status) return "-";
    const s = String(status).toLowerCase();
    if (s === "open" || s === "aberto") return i18n.t("reports.table.statusOptions.open", "Abierto");
    if (s === "pending" || s === "pendente") return i18n.t("reports.table.statusOptions.pending", "Pendiente");
    if (s === "closed" || s === "fechado") return i18n.t("reports.table.statusOptions.closed", "Cerrado");
    if (s === "group" || s === "grupo") return i18n.t("reports.table.statusOptions.group", "Grupo");
    if (s === "nps") return "NPS";
    if (s === "lgpd") return "LGPD";
    return status;
  };

  const formatSupportTime = (timeString) => {
    if (!timeString || timeString === "0") return "-";
    return timeString
      .replace(/hrs?\s*e?\s*/gi, "h ")
      .replace(/m\b/gi, "min")
      .replace(/,\s*$/g, "")
      .trim();
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts || []);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  const handleFilter = async (pageNumber, overrideLaneId = undefined, overrideBoardId = undefined) => {
    setLoading(true);
    try {
      const activeLane = overrideLaneId !== undefined ? overrideLaneId : selectedLane;
      const activeBoard = overrideBoardId !== undefined ? overrideBoardId : selectedBoard;
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: pageNumber,
        pageSize: pageSize,
        onlyRated: onlyRated ? "true" : "false",
        brandId: selectedBrand || undefined,
        kanbanBoardId: activeBoard || undefined,
        kanbanLaneId: activeLane || undefined,
        laneTagIds: selectedLaneTags.length > 0 ? JSON.stringify(selectedLaneTags) : undefined,
      });

      setTotalTickets(data.totalTickets ? data.totalTickets.total : 0);
      setTickets(data.tickets || []);
      setPageNumber(pageNumber);

      if (activeBoard) {
        fetchFunnelMetrics(activeBoard);
      }
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const exportarGridParaExcel = async () => {
    setLoading(true);
    try {
      const data = await getReport({
        searchParam,
        contactId: selectedContactId,
        whatsappId: JSON.stringify(selectedWhatsapp),
        users: JSON.stringify(userIds),
        queueIds: JSON.stringify(queueIds),
        status: JSON.stringify(selectedStatus),
        dateFrom,
        dateTo,
        page: 1,
        pageSize: 9999999,
        onlyRated: onlyRated ? "true" : "false",
        brandId: selectedBrand || undefined,
        kanbanBoardId: selectedBoard || undefined,
        kanbanLaneId: selectedLane || undefined,
        laneTagIds: selectedLaneTags.length > 0 ? JSON.stringify(selectedLaneTags) : undefined,
      });

      const ticketsData = (data.tickets || []).map((ticket) => {
        return {
          id: ticket.id,
          Conexión: ticket.whatsappName,
          Contacto: ticket.contactName,
          Usuario: ticket.userName,
          Fila: ticket.queueName,
          Estado: translateStatus(ticket.status),
          EtapaKanban: ticket.laneName || "-",
          LeadTimeEtapa: ticket.stageLeadTime || "-",
          ÚltimoMensaje: ticket.lastMessage,
          FechaApertura: ticket.createdAt,
          FechaCierre: ticket.closedAt,
          TiempoAtencion: formatSupportTime(ticket.supportTime),
          NPS: ticket.NPS || "",
          ValorOportunidad:
            ticket.opportunityValue !== undefined && ticket.opportunityValue !== null && parseFloat(ticket.opportunityValue) > 0
              ? `${currency} ${parseFloat(ticket.opportunityValue).toFixed(2)}`
              : ticket.valorVenda !== undefined && ticket.valorVenda !== null
              ? `${currency} ${ticket.valorVenda}`
              : "-",
          ValorDeVenta:
            ticket.valorVenda !== undefined && ticket.valorVenda !== null
              ? `${currency} ${ticket.valorVenda}`
              : "-",
          MotivoNoVenta: ticket.motivoNaoVenda || "-",
          FinalizadoConVenta:
            (ticket.finalizadoComVenda === true || ticket.stageRole === "won")
              ? i18n.t("common.yes", "Sí")
              : (ticket.stageRole === "lost" || Boolean(ticket.motivoNaoVenda))
              ? i18n.t("common.no", "No")
              : "-",
        };
      });

      const ws = XLSX.utils.json_to_sheet(ticketsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Atenciones");
      XLSX.writeFile(wb, `reporte-atenciones-${moment().format("YYYY-MM-DD")}.xlsx`);
    } catch (error) {
      toastError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectedUsers = (selecteds) => {
    setUserIds(selecteds.map((t) => t.id));
  };

  const handleSelectedStatus = (selecteds) => {
    setSelectedStatus(selecteds.map((t) => t.status));
  };

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />;
      default:
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />;
    }
  };

  const renderOption = (option) => {
    if (option.number) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {IconChannel(option.channel)}
          <Typography
            component="span"
            style={{
              fontSize: 14,
              marginLeft: "10px",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: "2",
            }}
          >
            {option.name} - {option.number}
          </Typography>
        </span>
      );
    }
    return `${i18n.t("newTicketModal.add", "Agregar")} ${option.name}`;
  };

  const handleSelectOption = (e, newValue) => {
    setSelectedContactId(newValue ? newValue.id : null);
    setSearchParam("");
  };

  const filter = createFilterOptions({ trim: true });

  const renderContactAutocomplete = () => {
    return (
      <Autocomplete
        fullWidth
        options={options}
        loading={loading}
        clearOnBlur
        autoHighlight
        freeSolo
        size="small"
        clearOnEscape
        getOptionLabel={(option) => option.number ? `${option.name} - ${option.number}` : (option.name || "")}
        renderOption={renderOption}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
            filtered.push({ name: `${params.inputValue}` });
          }
          return filtered;
        }}
        onChange={handleSelectOption}
        renderInput={(params) => (
          <TextField
            {...params}
            label={i18n.t("reports.filters.searchContact", "Buscar por Contacto / Cliente...")}
            variant="outlined"
            size="small"
            onChange={(e) => setSearchParam(e.target.value)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  };

  return (
    <MainContainer className={classes.mainContainer}>
      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.pageScrollArea}>
          {openTicketMessageDialog && ticketOpen && (
            <ShowTicketLogModal
              isOpen={openTicketMessageDialog}
              handleClose={() => setOpenTicketMessageDialog(false)}
              ticketId={ticketOpen.id}
            />
          )}

          {/* CABECERA SUPERIOR: SONAR INTERACTIVO Y TÍTULO HOMOLOGADO DENTRO DEL MARCO */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" style={{ marginTop: 28, marginBottom: 30, paddingLeft: 20, paddingRight: 20 }}>
            <Box display="flex" alignItems="center" style={{ gap: 14 }}>
              <PageHelpGuide
                helpData={REPORTS_HELP_DATA}
                tooltipText={i18n.t("reports.helpTooltip", "Guía de Reportes de Atenciones (Haz clic para ver)")}
              />
              <Typography
                variant="h5"
                style={{
                  fontWeight: 700,
                  color: theme.palette.type === "dark" ? "#FFF" : theme.palette.primary.main,
                  letterSpacing: "-0.5px",
                }}
              >
                {i18n.t("reports.title", "Reportes de Atenciones")}
              </Typography>
            </Box>
          </Box>

      {/* SCORECARD DE KPIS COMERCIALES */}
      <Box
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Card className={classes.kpiCard} variant="outlined">
          <CardContent style={{ padding: 16 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography className={classes.kpiTitle}>
                  {i18n.t("reports.kpi.totalTickets", "Total de Atenciones")}
                </Typography>
                <Typography className={classes.kpiValue}>
                  {kpis.totalCount}
                </Typography>
              </Box>
              <Equalizer style={{ fontSize: 32, color: theme.palette.primary.main }} />
            </Box>
          </CardContent>
        </Card>

        <Card className={classes.kpiCard} variant="outlined">
          <CardContent style={{ padding: 16 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography className={classes.kpiTitle}>
                  {i18n.t("reports.kpi.totalRevenue", "Monto Oportunidades")}
                </Typography>
                <Typography className={classes.kpiValue}>
                  {currency} {kpis.totalRevenue}
                </Typography>
              </Box>
              <AttachMoney style={{ fontSize: 32, color: "#4caf50" }} />
            </Box>
          </CardContent>
        </Card>

        <Card className={classes.kpiCard} variant="outlined">
          <CardContent style={{ padding: 16 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography className={classes.kpiTitle}>
                  {i18n.t("reports.kpi.lostRevenue", "Monto Perdido")}
                </Typography>
                <Typography className={classes.kpiValue}>
                  {currency} {kpis.lostRevenue}
                </Typography>
              </Box>
              <TrendingDown style={{ fontSize: 32, color: "#ef4444" }} />
            </Box>
          </CardContent>
        </Card>

        <Card className={classes.kpiCard} variant="outlined">
          <CardContent style={{ padding: 16 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography className={classes.kpiTitle}>
                  {i18n.t("reports.kpi.conversionRate", "Tasa de Conversión")}
                </Typography>
                <Typography className={classes.kpiValue}>
                  {kpis.conversionRate}%
                </Typography>
              </Box>
              <TrendingUp style={{ fontSize: 32, color: "#ff9800" }} />
            </Box>
          </CardContent>
        </Card>

        <Card className={classes.kpiCard} variant="outlined">
          <CardContent style={{ padding: 16 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography className={classes.kpiTitle}>
                  {i18n.t("reports.kpi.avgTicket", "Ticket Promedio")}
                </Typography>
                <Typography className={classes.kpiValue}>
                  {currency} {kpis.avgTicket}
                </Typography>
              </Box>
              <CheckCircle style={{ fontSize: 32, color: "#9c27b0" }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* GRÁFICO VISUAL DE EMBUDO (FUNNEL CHART) Y LEAD TIME */}
      <FunnelChart
        selectedBoard={selectedBoard}
        funnelData={funnelData}
        loading={loadingFunnel}
        selectedLane={selectedLane}
        onSelectStage={handleStageSelect}
        currency={currency}
      />

      {/* FILTROS JERÁRQUICOS EN CASCADA */}
      <Paper className={classes.mainPaperFilter} variant="outlined">
        <Box display="flex" alignItems="center" mb={1.5} style={{ gap: 6 }}>
          <FilterList style={{ fontSize: 20, color: theme.palette.primary.main }} />
          <Typography variant="subtitle2" style={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {i18n.t("reports.filters.hierarchicalTitle", "Filtros Jerárquicos de Marca y Embudo")}
          </Typography>
        </Box>

        {/* FILA 1: MARCA -> EMBUDO -> CONEXIÓN -> ETAPA -> SUB-ETIQUETA */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="brand-select-label">{i18n.t("reports.filters.brand", "Marca")}</InputLabel>
              <Select
                labelId="brand-select-label"
                value={selectedBrand}
                onChange={handleBrandChange}
                label={i18n.t("reports.filters.brand", "Marca")}
              >
                <MenuItem value="">
                  <em>{i18n.t("reports.filters.allBrands", "Todas las Marcas")}</em>
                </MenuItem>
                {brands.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md>
            <FormControl variant="outlined" size="small" fullWidth disabled={!selectedBrand && brands.length > 0}>
              <InputLabel id="board-select-label">{i18n.t("reports.filters.funnel", "Embudo (Tablero)")}</InputLabel>
              <Select
                labelId="board-select-label"
                value={selectedBoard}
                onChange={handleBoardChange}
                label={i18n.t("reports.filters.funnel", "Embudo (Tablero)")}
              >
                <MenuItem value="">
                  <em>{i18n.t("reports.filters.allFunnels", "Todos los Embudos")}</em>
                </MenuItem>
                {filteredBoards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel id="whatsapp-select-label">{i18n.t("reports.filters.connection", "Conexión")}</InputLabel>
              <Select
                labelId="whatsapp-select-label"
                multiple
                value={selectedWhatsapp}
                onChange={(e) => setSelectedWhatsapp(e.target.value)}
                label={i18n.t("reports.filters.connection", "Conexión")}
                renderValue={(selected) => {
                  if (selected.length === 0) return <em>{i18n.t("reports.filters.allConnections", "Todas")}</em>;
                  const names = selected.map((id) => {
                    const w = filteredWhatsapps.find((item) => item.id === id);
                    return w ? w.name : id;
                  });
                  return names.join(", ");
                }}
              >
                {filteredWhatsapps.map((w) => (
                  <MenuItem key={w.id} value={w.id}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md>
            <FormControl variant="outlined" size="small" fullWidth disabled={!selectedBoard}>
              <InputLabel id="lane-select-label">{i18n.t("reports.filters.stage", "Etapa (Lane)")}</InputLabel>
              <Select
                labelId="lane-select-label"
                value={selectedLane}
                onChange={handleLaneChange}
                label={i18n.t("reports.filters.stage", "Etapa (Lane)")}
              >
                <MenuItem value="">
                  <em>{i18n.t("reports.filters.allStages", "Todas las Etapas")}</em>
                </MenuItem>
                {lanes.map((lane) => (
                  <MenuItem key={lane.id} value={lane.id}>
                    {lane.name || lane.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md>
            <FormControl variant="outlined" size="small" fullWidth disabled={!selectedLane || laneTags.length === 0}>
              <InputLabel id="subtags-select-label">{i18n.t("reports.filters.subtags", "Sub-etiquetas")}</InputLabel>
              <Select
                labelId="subtags-select-label"
                multiple
                value={selectedLaneTags}
                onChange={(e) => setSelectedLaneTags(e.target.value)}
                label={i18n.t("reports.filters.subtags", "Sub-etiquetas")}
                renderValue={(selected) => {
                  if (selected.length === 0) return <em>{i18n.t("reports.filters.allSubtags", "Todas")}</em>;
                  const names = selected.map((id) => {
                    const tag = laneTags.find((t) => t.id === id);
                    return tag ? tag.name : id;
                  });
                  return names.join(", ");
                }}
              >
                {laneTags.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* FILA 2: CLIENTE, ASESOR, FILA, ESTADO */}
        <Box mt={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              {renderContactAutocomplete()}
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <UsersFilter onFiltered={handleSelectedUsers} style={{ padding: 0 }} />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <QueueSelectCustom
                selectedQueueIds={queueIds}
                onChange={(values) => setQueueIds(values)}
                style={{ marginTop: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <StatusFilter onFiltered={handleSelectedStatus} style={{ padding: 0 }} />
            </Grid>
          </Grid>
        </Box>

        {/* FILA 3: RANGO DE FECHAS Y ACCIONES */}
        <Box mt={2}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={i18n.t("reports.startDate", "Fecha de Inicio")}
                type="date"
                value={dateFrom}
                variant="outlined"
                fullWidth
                size="small"
                onChange={(e) => setDateFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                label={i18n.t("reports.endDate", "Fecha de Fin")}
                type="date"
                value={dateTo}
                variant="outlined"
                fullWidth
                size="small"
                onChange={(e) => setDateTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={6} style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={onlyRated}
                    onChange={() => setOnlyRated(!onlyRated)}
                  />
                }
                label={i18n.t("reports.buttons.onlyRated", "Solo Calificados")}
              />

              <Tooltip title={i18n.t("reports.buttons.exportExcel", "Exportar a Excel")}>
                <IconButton onClick={exportarGridParaExcel} color="primary">
                  <SaveAlt />
                </IconButton>
              </Tooltip>

              <Button
                variant="outlined"
                size="small"
                className={classes.applyButton}
                startIcon={<FilterList style={{ fontSize: 18 }} />}
                onClick={() => handleFilter(1)}
              >
                {i18n.t("reports.buttons.filter", "Filtrar")}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* TABLA DE ATENCIONES — 9 COLUMNAS SIN SCROLL HORIZONTAL */}
      <Paper className={classes.mainPaperTable} variant="outlined">
        <div className={classes.tableScrollArea}>
          <Table size="small" id="grid-attendants" style={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" className={classes.tableHeaderCell} style={{ width: "6%" }}>
                  {i18n.t("reports.table.id", "Ticket #")}
                </TableCell>
                <TableCell align="left" className={classes.tableHeaderCell} style={{ width: "15%" }}>
                  {i18n.t("reports.table.contactConnection", "Contacto / Conexión")}
                </TableCell>
                <TableCell align="left" className={classes.tableHeaderCell} style={{ width: "13%" }}>
                  {i18n.t("reports.table.userQueue", "Asesor / Departamento")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeaderCell} style={{ width: "13%" }}>
                  {i18n.t("reports.table.statusStage", "Estado / Etapa")}
                </TableCell>
                <TableCell align="left" className={classes.tableHeaderCell} style={{ width: "17%" }}>
                  {i18n.t("reports.table.lastMessage", "Último mensaje recibido")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeaderCell} style={{ width: "12%" }}>
                  {i18n.t("reports.table.dates", "Apertura / Cierre")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeaderCell} style={{ width: "10%" }}>
                  {i18n.t("reports.table.times", "Atención / Lead Time")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeaderCell} style={{ width: "8%" }}>
                  {i18n.t("reports.table.valueDeal", "Valor / NPS")}
                </TableCell>
                <TableCell align="center" className={classes.tableHeaderCell} style={{ width: "6%" }}>
                  {i18n.t("reports.table.actions", "Acciones")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id} hover className={classes.tableRowCustom}>
                  {/* 1. Ticket # */}
                  <TableCell align="center" className={classes.tableCellMiddle}>
                    <strong style={{ color: theme.palette.primary.main, fontSize: "0.85rem" }}>#{ticket.id}</strong>
                  </TableCell>

                  {/* 2. Contacto / Conexión */}
                  <TableCell align="left" className={classes.tableCellMiddle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <Typography style={{ fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.25 }}>
                        {ticket.contactName || "-"}
                      </Typography>
                      <Typography style={{ fontSize: "0.74rem", color: "#8C939D", lineHeight: 1.2 }}>
                        {ticket.whatsappName || "-"}
                      </Typography>
                    </div>
                  </TableCell>

                  {/* 3. Usuario / Departamento */}
                  <TableCell align="left" className={classes.tableCellMiddle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <Typography style={{ fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.25 }}>
                        {ticket.userName || "-"}
                      </Typography>
                      <Typography style={{ fontSize: "0.74rem", color: "#8C939D", lineHeight: 1.2 }}>
                        {ticket.queueName || "-"}
                      </Typography>
                    </div>
                  </TableCell>

                  {/* 4. Estado / Etapa Kanban */}
                  <TableCell align="center" className={classes.tableCellMiddle}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: 4,
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          backgroundColor:
                            ticket.status === "closed" || ticket.status === "FECHADO"
                              ? "rgba(76, 175, 80, 0.15)"
                              : `${theme.palette.primary.main}26`,
                          color:
                            ticket.status === "closed" || ticket.status === "FECHADO"
                              ? "#4caf50"
                              : theme.palette.primary.main,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {translateStatus(ticket.status)}
                      </span>
                      {ticket.laneName ? (
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 4,
                            fontSize: "0.72rem",
                            fontWeight: 600,
                            backgroundColor: ticket.laneColor ? `${ticket.laneColor}26` : `${theme.palette.primary.main}26`,
                            color: ticket.laneColor || theme.palette.primary.main,
                            border: `1px solid ${ticket.laneColor || theme.palette.primary.main}66`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {ticket.laneName}
                        </span>
                      ) : (
                        <span style={{ fontSize: "0.72rem", color: "#8C939D" }}>-</span>
                      )}
                    </div>
                  </TableCell>

                  {/* 5. Último mensaje recibido */}
                  <TableCell align="left" className={classes.tableCellMiddle}>
                    <Tooltip title={ticket.lastMessage || ""} arrow placement="top">
                      <Typography className={classes.truncatedMessage}>
                        {ticket.lastMessage || "-"}
                      </Typography>
                    </Tooltip>
                  </TableCell>

                  {/* 6. Fechas Apertura / Cierre */}
                  <TableCell align="center" className={classes.tableCellMiddle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <Typography style={{ fontSize: "0.76rem", lineHeight: 1.25 }}>
                        {ticket.createdAt || "-"}
                      </Typography>
                      <Typography style={{ fontSize: "0.74rem", color: "#8C939D", lineHeight: 1.2 }}>
                        {ticket.closedAt ? ticket.closedAt : "-"}
                      </Typography>
                    </div>
                  </TableCell>

                  {/* 7. Tiempos (Atención / Lead Time) */}
                  <TableCell align="center" className={classes.tableCellMiddle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      <Typography style={{ fontSize: "0.76rem", lineHeight: 1.25 }}>
                        {formatSupportTime(ticket.supportTime)}
                      </Typography>
                      {ticket.stageLeadTime ? (
                        <span style={{ fontSize: "0.76rem", color: "#ffb300", fontWeight: 600 }}>
                          {ticket.stageLeadTime}
                        </span>
                      ) : (
                        <span style={{ fontSize: "0.74rem", color: "#8C939D" }}>-</span>
                      )}
                    </div>
                  </TableCell>

                  {/* 8. Valor / NPS */}
                  <TableCell align="center" className={classes.tableCellMiddle}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {ticket.opportunityValue !== undefined && ticket.opportunityValue !== null && parseFloat(ticket.opportunityValue) > 0 ? (
                        <span style={{ color: "#4caf50", fontWeight: 700, fontSize: "0.82rem" }}>
                          {`${currency} ${parseFloat(ticket.opportunityValue).toFixed(2)}`}
                        </span>
                      ) : ticket.valorVenda !== undefined && ticket.valorVenda !== null && parseFloat(ticket.valorVenda) > 0 ? (
                        <span style={{ color: "#4caf50", fontWeight: 700, fontSize: "0.82rem" }}>
                          {`${currency} ${parseFloat(ticket.valorVenda).toFixed(2)}`}
                        </span>
                      ) : (
                        <span style={{ fontSize: "0.8rem", color: "#8C939D" }}>-</span>
                      )}
                      {ticket.NPS ? (
                        <span style={{ fontSize: "0.72rem", color: "#8C939D" }}>
                          NPS: {ticket.NPS}
                        </span>
                      ) : null}
                    </div>
                  </TableCell>

                  {/* 9. Acciones */}
                  <TableCell align="center" className={classes.tableCellMiddle}>
                    <Box display="flex" justifyContent="center" alignItems="center">
                      <Tooltip title={i18n.t("reports.actions.ticketLogs", "Historial del Ticket")}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setOpenTicketMessageDialog(true);
                            setTicketOpen(ticket);
                          }}
                        >
                          <History fontSize="small" style={{ color: blue[700] }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={i18n.t("reports.actions.accessTicket", "Ir al Ticket")}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            history.push(`/tickets/${ticket.uuid}`);
                          }}
                        >
                          <Forward fontSize="small" style={{ color: green[700] }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {loading && <TableRowSkeleton avatar columns={9} />}

              {!loading && tickets.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ padding: 32 }}>
                    <Typography variant="body1" color="textSecondary">
                      {i18n.t("reports.table.empty", "No se encontraron atenciones con los filtros seleccionados.")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Paper>

      {/* PAGINACIÓN */}
      <Box mt={2}>
        <Grid container alignItems="center">
          <Grid item xs={12} sm={10} md={10}>
            <Pagination
              count={Math.ceil(totalTickets / pageSize)}
              page={pageNumber}
              onChange={(event, value) => handleFilter(value)}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={2} md={2}>
            <FormControl margin="dense" variant="outlined" fullWidth size="small">
              <InputLabel>{i18n.t("tickets.search.ticketsPerPage", "Atenciones por página")}</InputLabel>
              <Select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(e.target.value);
                  handleFilter(1);
                }}
                label={i18n.t("tickets.search.ticketsPerPage", "Atenciones por página")}
              >
                <MenuItem value={5}>5</MenuItem>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </div>
  </Paper>
</MainContainer>
);
};

export default Reports;
